import React, { useEffect, useState, useCallback } from "react";
import { RefreshCw, Grid3x3, ChevronDown, ChevronRight } from "lucide-react";
import { getAnalyses } from "@/lib/api";
import { fmt } from "@/lib/format";

const METRICS = [
  { key: "rsi", label: "RSI" },
  { key: "change_pct", label: "1W %" },
  { key: "dist_52w_high", label: "52W HI" },
];

function tint(metric, v) {
  if (v === null || v === undefined) return { bg: "#F8FAFC", bd: "#E2E8F0", fg: "#94A3B8", tag: "—", txt: "—" };
  if (metric === "rsi") {
    if (v >= 70) return { bg: "#FEE2E2", bd: "#FCA5A5", fg: "#991B1B", tag: "OVERBOUGHT", txt: fmt(v, 0) };
    if (v >= 60) return { bg: "#FEF2F2", bd: "#FECACA", fg: "#B91C1C", tag: "HOT", txt: fmt(v, 0) };
    if (v <= 30) return { bg: "#DCFCE7", bd: "#86EFAC", fg: "#065F46", tag: "OVERSOLD", txt: fmt(v, 0) };
    if (v <= 40) return { bg: "#ECFDF5", bd: "#A7F3D0", fg: "#047857", tag: "COOL", txt: fmt(v, 0) };
    return { bg: "#FFFFFF", bd: "#E2E8F0", fg: "#334155", tag: "NEUTRAL", txt: fmt(v, 0) };
  }
  if (metric === "change_pct") {
    const txt = `${v > 0 ? "+" : ""}${fmt(v, 1)}%`;
    if (v >= 2) return { bg: "#DCFCE7", bd: "#86EFAC", fg: "#065F46", tag: "SURGING", txt };
    if (v > 0) return { bg: "#ECFDF5", bd: "#A7F3D0", fg: "#047857", tag: "UP", txt };
    if (v <= -2) return { bg: "#FEE2E2", bd: "#FCA5A5", fg: "#991B1B", tag: "FALLING", txt };
    return { bg: "#FEF2F2", bd: "#FECACA", fg: "#B91C1C", tag: "DOWN", txt };
  }
  // dist_52w_high (0 = at high, negative = below)
  const txt = `${fmt(v, 1)}%`;
  if (v >= -3) return { bg: "#FEE2E2", bd: "#FCA5A5", fg: "#991B1B", tag: "AT HIGHS", txt };
  if (v <= -20) return { bg: "#DCFCE7", bd: "#86EFAC", fg: "#065F46", tag: "DEEP DIP", txt };
  if (v <= -10) return { bg: "#ECFDF5", bd: "#A7F3D0", fg: "#047857", tag: "PULLBACK", txt };
  return { bg: "#FFFFFF", bd: "#E2E8F0", fg: "#334155", tag: "MID-RANGE", txt };
}

export default function SectorHeatmap({ title, sectors, universe, onSelect, refreshKey, testid }) {
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("rsi");
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getAnalyses({ universe })
      .then((d) => {
        const m = {};
        (d.items || []).forEach((x) => { m[x.ticker] = x; });
        setMap(m);
      })
      .finally(() => setLoading(false));
  }, [universe]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-[4px] shadow-xs p-3.5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setCollapsed((v) => !v)} data-testid={`${testid}-collapse`} className="text-slate-400 hover:text-slate-700 transition-colors">
            {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </button>
          <Grid3x3 size={15} className="text-[#0070BA]" />
          <span className="font-bold text-[13px] tracking-[0.1em] uppercase text-slate-700">{title}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex gap-0.5 p-0.5 bg-[#F1F5F9] rounded-[4px]">
            {METRICS.map((mt) => (
              <button
                key={mt.key}
                onClick={() => setMetric(mt.key)}
                data-testid={`${testid}-metric-${mt.key}`}
                className={`font-semibold text-[10px] tracking-wide px-2 py-1 rounded-[3px] transition-colors ${metric === mt.key ? "bg-[#121D28] text-white" : "text-slate-500 hover:text-slate-800"}`}
              >
                {mt.label}
              </button>
            ))}
          </div>
          <button onClick={load} data-testid={`${testid}-refresh`} className="text-slate-400 hover:text-slate-700 transition-colors">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>
      {!collapsed && (
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {sectors.map((s) => {
          const d = map[s.t];
          const c = tint(metric, d ? d[metric] : null);
          return (
            <button
              key={s.t}
              onClick={() => d && onSelect(s.t)}
              data-testid={`sector-tile-${s.t.replace("^", "")}`}
              className="text-left rounded-[4px] border p-2.5 transition-transform duration-150 hover:-translate-y-0.5"
              style={{ background: c.bg, borderColor: c.bd }}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] font-bold text-slate-900 truncate">{s.name}</span>
                <span className="font-mono text-[13px] font-bold tabular-nums shrink-0" style={{ color: c.fg }}>{c.txt}</span>
              </div>
              <div className="text-[9px] font-semibold tracking-wide mt-1" style={{ color: c.fg }}>{c.tag}</div>
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
}
